/**
 * Regions, in the client — cities plan C2.1 (D1).
 *
 * A region is a row (`regions`, C0.1): Berlin and Boston, each with a zone,
 * locale, currency, holidays key and a map centre. `region_towns` says which
 * towns belong to a region and at which reach, and replaced both SQL's
 * `is_day_trip()` array and `reach.ts`'s mirrored `DAY_TRIP_CITIES` (trap
 * T4). `useRegions()` reads the three tables; the copy below is what the app
 * knows before they answer, offline, or while they do not exist yet, and
 * `test/regions-client.test.ts` holds it to the latest migration.
 *
 * Two things read from here everywhere, and are installed into `zone.ts`
 * so files that cannot import React can ask for them:
 *
 *   - the current region's zone — "today". The current region is whatever
 *     `currentRegion.ts` resolved (override > stay > home, C2.2) and
 *     published here with `setActiveRegionId`; until it has, the home region.
 *   - a row's zone: `regionOf(row.city, towns)` → that region's `timezone`,
 *     or `FALLBACK_ZONE` where no region claims the town (trap T2: a row with
 *     no city is a Berlin row until C1.x writes one on every row).
 */
import { useEffect } from 'react'
import { regionOf, reachOf, type Reach } from './reach.ts'
import { FALLBACK_ZONE, setZones, type Zones } from './zone.ts'
import { useRegionsQuery } from './queries'
import { ORTSTEILE } from '../../../src/districts.ts'

export { regionOf, reachOf, type Reach }

export interface Region {
  id: string
  name: string
  country: string
  timezone: string
  locale: string
  currency: string
  holidays: string | null
  lat: number
  lng: number
  zoom: number
}

export interface RegionTown {
  region_id: string
  town: string
  tier: 'core' | 'day_trip'
}

export interface District {
  ortsteil: string
  bezirk: string | null
  region_id: string
}

/** What `useRegions()` hands a screen. */
export interface RegionsData {
  regions: Map<string, Region>
  towns: RegionTown[]
  districts: District[]
}

/** The three tables as they come off the wire, and as the persisted cache
 *  holds them — plain arrays, so the query's `select` builds the Map. */
export interface RegionsWire {
  regions: Region[]
  towns: RegionTown[]
  districts: District[]
}

/** The region a person's Year calls home when the profile names none:
 *  `profiles.home_region` is C0.2's column, read with `?? 'berlin'`. */
export const HOME_REGION_ID = 'berlin'

/**
 * The region being looked at (C2.2). Ambient, like `zone.ts`'s two zones and
 * for the same reason: `currentRegion(data)` is called from twenty places that
 * cannot all take a hook, and `hereZone()` answers from it.
 *
 * It is *published* by `currentRegion.ts` (`useCurrentRegion`, override >
 * stay > home) during the render of the app root, before any child reads it,
 * and **setting it renders nothing**: a component that read the old value
 * keeps it until something re-renders it. That is why a switch is a key on the
 * app root (`RegionRoot`), not this setter alone — C2.1's worker warned of
 * exactly this about `setZones`.
 */
let activeRegionId = HOME_REGION_ID

export function setActiveRegionId(id: string): void {
  activeRegionId = id
}

/** The id `currentRegion` answers with. */
export function getActiveRegionId(): string {
  return activeRegionId
}

// ---------------------------------------------------------------------------
// The bundled copy — C0.1's rows, for offline tests and the first render
// ---------------------------------------------------------------------------

/** C0.1's two rows. Berlin's zone is spelled `FALLBACK_ZONE`, which is
 *  `'Europe/Berlin'` by definition (trap T2: a row no region claims is a
 *  Berlin row), so the literal lives in one place; the agreement test reads
 *  the value, not the spelling. */
export const BUNDLED_REGIONS: Region[] = [
  {
    id: 'berlin', name: 'Berlin', country: 'DE', timezone: FALLBACK_ZONE, locale: 'de-DE',
    currency: 'EUR', holidays: 'DE-BE', lat: 52.52, lng: 13.405, zoom: 12,
  },
  {
    id: 'boston', name: 'Boston', country: 'US', timezone: 'America/New_York', locale: 'en-US',
    currency: 'USD', holidays: 'US-MA', lat: 42.3601, lng: -71.0589, zoom: 11,
  },
]

const town = (region_id: string, tier: RegionTown['tier']) => (t: string): RegionTown => ({ region_id, town: t, tier })

/** Berlin's day trips are the live `is_day_trip()` array, same spelling —
 *  lowercase, because that is how the SQL held them, and "brandenburg" bare,
 *  which is what `venues.city` says for the Brandenburg galleries (DAS MINSK,
 *  ROHKUNSTBAU). Boston's are `docs/cities-plan/README.md`'s *Boston's towns*,
 *  as `venues.city` will spell them. */
export const BUNDLED_TOWNS: RegionTown[] = [
  // 'BER' and 'BOS' are core towns too: the travel pass labels a stay with
  // the airport code when IATA_INFO does not know the city (C0.2).
  ...['Berlin', 'BER'].map(town('berlin', 'core')),
  ...[
    'brandenburg', 'brandenburg an der havel', 'potsdam', 'chorin',
    'dessau-roßlau', 'dessau-rosslau', 'dresden', 'leipzig', 'altdöbern',
    'frankfurt (oder)', 'cottbus', 'wittenberg', 'lutherstadt wittenberg',
    'rheinsberg', 'neuruppin', 'eberswalde', 'oranienburg', 'uckermark', 'halle',
    'halle (saale)',
    'altfriedland', 'altkünkendorf', 'angermünde', 'bad belzig', 'battin',
    'beeskow', 'berkholz', 'bernau bei berlin', 'biesenbrow', 'birkenwerder',
    'blankenfelde-mahlow', 'blankensee', 'boitzenburg', 'bollmannsruh',
    'borgsdorf', 'bornsdorf', 'brodowin', 'brüssow',
    'buckow (märkische schweiz)', 'bülowssiege', 'caputh', 'casekow',
    'dobberzin', 'drebkau', 'fehrbellin', 'finsterwalde', 'friedenfelde',
    'friedland', 'fürstenwalde/spree', 'gerswalde', 'glashütte', 'glindow',
    'greiffenberg', 'groß behnitz', 'groß dölln', 'groß fredenwalde',
    'groß pankow', 'heiligengrabe', 'herzfelde', 'hohen neuendorf',
    'jakobshagen', 'joachimsthal', 'kleßen', 'kloster zinna',
    'königs wusterhausen', 'langen', 'lebusa', 'lehnin',
    'lichterfeld-schacksdorf', 'liebenberg', 'lindow (mark)', 'lobetal',
    'luckau', 'lübbenau/spreewald', 'milow', 'mürow', 'nauen', 'nennhausen',
    'neustadt (dosse)', 'niedergörsdorf', 'oderaue', 'oranienbaum-wörlitz',
    'paretz', 'parlow-glambeck', 'perleberg', 'petzow', 'polßen', 'prenzlau',
    'putlitz', 'päwesin', 'reckahn', 'ribbeck', 'schwedt/oder', 'schönfließ',
    'stechau', 'stolpe', 'straupitz', 'strausberg', 'temmen', 'temnitztal',
    'templin', 'wahrenbrück', 'wandlitz', 'warnitz', 'wiesenburg', 'wittenberge',
    'wolfshagen', 'wriezen',
  ].map(town('berlin', 'day_trip')),
  ...[
    'Boston', 'BOS', 'Cambridge', 'Somerville', 'Brookline', 'Concord', 'Lincoln',
    'Lexington', 'Bedford', 'Acton', 'Carlisle', 'Sudbury', 'Maynard', 'Arlington',
    'Belmont', 'Watertown', 'Medford', 'Malden', 'Everett', 'Chelsea', 'Waltham',
    'Newton', 'Wellesley', 'Needham', 'Dedham', 'Quincy', 'Winchester', 'Stoneham',
    'Woburn', 'Burlington', 'Natick', 'Framingham', 'Littleton', 'Westford', 'Stow',
    'Wayland', 'Weston',
  ].map(town('boston', 'core')),
  ...[
    'Salem', 'Beverly', 'Gloucester', 'Rockport', 'Marblehead', 'Newburyport',
    'Lowell', 'Lawrence', 'Andover', 'Worcester', 'Fitchburg', 'Shirley', 'Groton',
    'Harvard', 'Bolton', 'Providence', 'Portsmouth', 'Plymouth', 'Northampton',
    'Amherst', 'Williamstown', 'North Adams', 'Lenox', 'Stockbridge',
  ].map(town('boston', 'day_trip')),
]

/** Berlin's Ortsteile from `src/districts.ts`, the list `districts` was
 *  seeded from (C0.1 gives every one of its rows `berlin`). Boston's
 *  neighbourhoods arrive with C3.0, live only. */
const BUNDLED_DISTRICTS: District[] = [
  ...Object.entries(ORTSTEILE).map(([ortsteil, bezirk]): District => ({ ortsteil, bezirk, region_id: 'berlin' })),
  // The live table has Potsdam as a row of its own with no Bezirk, which is
  // what lets `whereOf` fold a Potsdam venue to 'Potsdam' without a special
  // case (C2.2); `src/districts.ts` knows it only as `toOrtsteil`'s answer.
  { ortsteil: 'Potsdam', bezirk: null, region_id: 'berlin' },
]

export const BUNDLED_WIRE: RegionsWire = {
  regions: BUNDLED_REGIONS, towns: BUNDLED_TOWNS, districts: BUNDLED_DISTRICTS,
}

/** The query's `select`: plain arrays in, the Map a screen wants out.
 *  Module-level so the select keeps its identity and TanStack memoises it. */
export function regionsData(w: RegionsWire): RegionsData {
  return { regions: new Map(w.regions.map(r => [r.id, r])), towns: w.towns, districts: w.districts }
}

/** What every reader falls back to until the live tables answer. */
export const BUNDLED: RegionsData = regionsData(BUNDLED_WIRE)

// ---------------------------------------------------------------------------
// Reading it
// ---------------------------------------------------------------------------

/** The region being looked at, from whatever regions are known — never
 *  undefined, because the bundled copy always has it. */
export function currentRegion(data: RegionsData): Region {
  return data.regions.get(activeRegionId) ?? BUNDLED.regions.get(activeRegionId)
    ?? BUNDLED.regions.get(HOME_REGION_ID)!
}

/** The id of the region a person calls home. `profiles.home_region` arrives
 *  with C0.2; until then no profile carries it. */
export function homeRegionOf(profile: object): string {
  return (profile as { home_region?: string | null }).home_region ?? HOME_REGION_ID
}

/** The name the Year prints for its home periods ("Berlin"), the Year's
 *  `'Home'` token being only a token (trap T1). */
export function homeName(data: RegionsData, profile: object): string {
  const id = homeRegionOf(profile)
  return (data.regions.get(id) ?? BUNDLED.regions.get(id) ?? currentRegion(data)).name
}

/** The two zones `zone.ts` answers with, from `data`. The town lookup is
 *  memoised per city: a feed asks about the same few hundred towns on every
 *  render, and `regionOf` lowercases its argument each time. */
export function zonesOf(data: RegionsData): Zones {
  const hit = zonesCache.get(data)
  if (hit) return hit
  const memo = new Map<string, string>()
  const zones: Zones = {
    // A getter, not the value: this object is cached per `data`, and the
    // current region moves under it (C2.2). Read at construction, `here`
    // would be the zone of whichever region was current the first time anyone
    // asked, for as long as the regions did not change.
    get here() { return currentRegion(data).timezone },
    of: city => {
      const key = city ?? ''
      let tz = memo.get(key)
      if (tz === undefined) {
        const id = regionOf(city ?? null, data.towns)
        // T2: no city, or a town no region lists (London, Dessau-Roßlau), is
        // read as Berlin — every such row is a Berlin source's until C1.x.
        tz = (id && data.regions.get(id)?.timezone) || FALLBACK_ZONE
        memo.set(key, tz)
      }
      return tz
    },
  }
  zonesCache.set(data, zones)
  return zones
}
/** One `Zones` per regions object: every screen that reads `useRegions()`
 *  installs the same one, and its town memo survives. */
const zonesCache = new WeakMap<RegionsData, Zones>()

// The first render reads the bundled copy, so a cold load paints the same
// clocks it painted before this file existed.
setZones(zonesOf(BUNDLED))

/**
 * The regions, their towns and their districts, from `regions`,
 * `region_towns` and `districts` — persisted, a day's `staleTime`.
 *
 * Undefined until the query has answered (or a persisted answer hydrated);
 * a reader that cannot wait uses `useRegions() ?? BUNDLED`. When the query
 * fails — the tables do not exist until C0.1 is applied, and an offline
 * first start has nothing to read — the bundled copy stands in.
 */
export function useRegions(): RegionsData | undefined {
  const q = useRegionsQuery(regionsData)
  const data = q.data ?? (q.isError ? BUNDLED : undefined)
  useEffect(() => { if (data) setZones(zonesOf(data)) }, [data])
  return data
}

/** `useRegions()`, with the bundled copy while it loads: for a screen that
 *  must draw on its first render and cannot draw "loading". */
export function useRegionsNow(): RegionsData {
  return useRegions() ?? BUNDLED
}
